/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { ReadOnlyPropertiesMixin } from '@open-wc/lit-helpers';
import { html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { calculatedBaseFontSize } from '../../utils/base-font-size.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { KEYCODE } from '../../utils/keycode.js';
import { convertToPixel } from '../../utils/rem-to-pixel-convertor.js';
import { SettingTooltipMixin } from '../../utils/setting-tooltip-mixin.js';
import { SliderInputRectifierMixin } from '../../utils/slider-input-rectifier-mixin.js';
import { SliderStepMarkerMixin } from '../../utils/slider-step-marker-mixin.js';
import './arrow-controls.js';
import './range-slider.js';
import { sliderStyles } from './sh-slider-styles.js';

const TICK_RADIUS = 3 / calculatedBaseFontSize;

/**
 * @prop {String} label - Defines the label of the slider. Will be shown as a placeholder on top of it or hidden if it's empty or undefined. It is only available in the horizontal direction.
 * @prop {Number} value - Default 0. Defines the current value of the slider. Must be a number between the min and max values.
 * @prop {Number} step - Default 1. Defines the intervals of the slider. If set to 0.1 for example, decimals will be allowed.
 * @prop {Boolean} showValue - Default false. If set to `true`, an input field will be shown allowing the user to change the value using keyboard input.
 * @prop {Number} min - Default 0. Defines the minimum value allowed.
 * @prop {Number} max - Default 100. Defines the maximum value allowed.
 * @attr {Boolean} show-value - Default false. If set to true, an input field will be shown allowing the user to change the value using keyboard input.
 * @prop {Boolean} disabled - Default false. If set to `true`, user interaction won't be allowed and the styles of the component will change.
 * @prop {Boolean} showControls - Default false. If set to `true`, user can adjust the slider value using arrows. This feature is not available for range.
 * @attr {Boolean} show-controls - Default false. If set to true, user can adjust the slider value using arrows. This feature is not available for range.
 * @prop {Boolean} showRange - Default false. If set to `true`, slider is modified to range.
 * @attr {Boolean} show-range - Default false. If set to true, slider is modified to range.
 * @prop {Number} upperValue - Default 0. Defines the current upper value of the range in a slider . Must be a number between min and max values and cannot be less than the lower value.
 * @attr {String} upper-value - Default 0. Defines the current upper value of the range in a slider . Must be a number between min and max values and cannot be less than the lower value.
 * @prop {Number} lowerValue - Default 0. Defines the current lower value of the range in a slider . Must be a number between min and max values and cannot be more than the upper value.
 * @attr {String} lower-value - Default 0. Defines the current lower value of the range in a slider . Must be a number between min and max values and cannot be more than the upper value.
 * @prop {Array} ticks - A set of numbers in between the min and max range of the slider where markers will be displayed. Range related properties like show-range, upper-value and lower-value will not be available when the markers property has been defined.
 * @prop {Boolean} vertical - Default false. If set to `true`, the slider will have a vertical orientation.
 * @prop {Boolean} directionToggle - If set to `true`, the vertical slider's min and max positions are interchanged. It only works in combination with the vertical property.
 * @attr {Boolean} direction-toggle - If set to true, the vertical slider's min and max positions are interchanged. It only works in combination with the vertical property.
 * @prop {Boolean} right - Default false. If vertical is also set to true, the slider tooltip will be shown on the left side.
 * @prop {Boolean} stepMarkers - *hide Shows markers if enabled.
 */
export class SHSlider extends SettingTooltipMixin(
  SliderStepMarkerMixin(SliderInputRectifierMixin(ReadOnlyPropertiesMixin(LitElement)))
) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: Number, reflect: true },
      min: { type: Number, reflect: true },
      max: { type: Number, reflect: true },
      step: { type: Number, reflect: true },
      /** @ignore */
      showValue: { type: Boolean, reflect: true, attribute: 'show-value' },
      disabled: { type: Boolean, reflect: true },
      /** @ignore */
      showControls: {
        type: Boolean,
        reflect: true,
        attribute: 'show-controls',
      },
      /** @ignore */
      showRange: { type: Boolean, reflect: true, attribute: 'show-range' },
      /** @ignore */
      upperValue: { type: Number, reflect: true, attribute: 'upper-value' },
      /** @ignore */
      lowerValue: { type: Number, reflect: true, attribute: 'lower-value' },
      ticks: { type: Array, reflect: true },
      vertical: { type: Boolean, reflect: true },
      /** @ignore */
      directionToggle: {
        type: Boolean,
        reflect: true,
        attribute: 'direction-toggle',
      },
      right: { type: Boolean, reflect: true },
      _renderedTicks: { type: Array, readOnly: true },
      stepMarkers: { type: Array, readOnly: true },
      /** @ignore */
      _selectedThumb: { type: String },
      /** @ignore */
      _isDrag: { type: Boolean, reflect: true },
      /** @ignore */
      _ticksRendered: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return sliderStyles;
  }

  render() {
    return html`
      ${this.label && !this.showRange
        ? html` <div class="label-wrapper">
            <div class="slider-label">${this.label}</div>
            ${!this.vertical
              ? html` <input
                  id="inputText"
                  type="number"
                  min="${this.min}"
                  max="${this.max}"
                  step="${this.step}"
                  .value="${this.value}"
                  ?disabled="${this.disabled}"
                  @keyup="${this._onKeyPress}"
                  @blur="${this._handleOnBlur}"
                />`
              : ''}
          </div>`
        : ''}
      ${!this.showRange
        ? html` ${!this.vertical
            ? // slider implementation with adjustment controls
              html` <div class="outer-wrapper">
                <div class="sliderWrapper">
                  <input
                    type="range"
                    id="horizontalSlider"
                    class="sliderTrack"
                    min="${this.min}"
                    max="${this.max}"
                    label="${this.label}"
                    step="${this.step}"
                    .value="${this.value}"
                    ?disabled="${this.disabled}"
                    @mousedown="${this._thumbDown}"
                    @mousemove="${this._thumbMove}"
                    @mouseup="${this._thumbUp}"
                    @touchstart="${this._thumbDown}"
                    @touchmove="${this._thumbMove}"
                    @touchend="${this._thumbUp}"
                    @click="${this._updateValue}"
                  />
                  <div id="sliderTooltip">${this.value}</div>
                  <ul id="step_ticks" class="step_ticks">
                    ${this.stepMarkers.map(
                      (item) => html` <li
                        class="sh-step-thumb-item"
                        @click="${(e) => this._onTickClick(e)}"
                        data-value="${item.position}"
                        style="${item.style}"
                      ></li>`
                    )}
                  </ul>
                  ${this.ticks
                    ? html` <ul id="ticks_list" class="ticks-list">
                        ${this._renderedTicks.map(
                          (item) => html` <li
                            class="tick-item"
                            data-value="${item.position}"
                            style="${item.style}"
                            @click="${(e) => this._onTickClick(e)}"
                          ></li>`
                        )}
                      </ul>`
                    : ''}
                </div>
                ${this.showControls
                  ? html` <arrow-controls
                      horizontal
                      .parentElement="${this}"
                      .min="${this.min}"
                      .max="${this.max}"
                      .step="${this.step}"
                      .value="${this.value}"
                      ?disabled="${this.disabled}"
                    ></arrow-controls>`
                  : ''}
              </div>`
            : // vertical slider implementation
              html` <div id="inputLabel">
                  <input
                    type="number"
                    id="verticalInput"
                    min="${this.min}"
                    max="${this.max}"
                    step="${this.step}"
                    .value="${this.value}"
                    ?disabled="${this.disabled}"
                    @keyup="${this._onKeyPress}"
                    @blur="${this._handleOnBlur}"
                    @focus="${this._verticalInputClickActions}"
                    @focusout="${this._verticalInputFousOutActions}"
                  />
                  <span
                    id="verticalMax"
                    @click="${this._verticalInputClickActions}"
                    @focusout="${this._verticalInputFousOutActions}"
                    >/${this.max}</span
                  >
                </div>
                <div class="vertical-slider-wrapper">
                  <div class="vertical-wrapper">
                    <input
                      type="range"
                      id="verticalSlider"
                      class="verticalTrack"
                      min="${this.min}"
                      max="${this.max}"
                      label="${this.label}"
                      step="${this.step}"
                      .value="${this.value}"
                      ?disabled="${this.disabled}"
                      @mousedown="${this._thumbDown}"
                      @mousemove="${this._thumbMove}"
                      @mouseup="${this._thumbUp}"
                      @touchstart="${this._thumbDown}"
                      @touchmove="${this._thumbMove}"
                      @touchend="${this._thumbUp}"
                      @click="${this._updateValue}"
                    />
                    <div id="verticalTooltip">${this.value}</div>
                    <ul id="vertical_step_ticks" class="step_ticks">
                      ${this.stepMarkers.map(
                        (item) => html` <li
                          class="sh-step-thumb-item"
                          @click="${(e) => this._onTickClick(e)}"
                          data-value="${item.position}"
                          style="${item.style}"
                        ></li>`
                      )}
                    </ul>
                    ${this.ticks
                      ? html` <ul id="ticks_list" class="ticks-list">
                          ${this._renderedTicks.map(
                            (item) => html` <li
                              class="tick-item"
                              data-value="${item.position}"
                              style="${item.style}"
                              @click="${(e) => this._onTickClick(e)}"
                            ></li>`
                          )}
                        </ul>`
                      : ''}
                  </div>
                </div>
                ${this.showControls
                  ? html` <arrow-controls
                      .parentElement="${this}"
                      .min="${this.min}"
                      .max="${this.max}"
                      .step="${this.step}"
                      .value="${this.value}"
                      ?disabled="${this.disabled}"
                    ></arrow-controls>`
                  : ''}`}`
        : // range slider implementation
          html`
            <range-slider
              .label="${this.label}"
              .min="${this.min}"
              .max="${this.max}"
              .step="${this.step}"
              .lowerValue="${this.lowerValue}"
              .upperValue="${this.upperValue}"
              .showValue="${this.showValue}"
              ?disabled="${this.disabled}"
            ></range-slider>
          `}
    `;
  }

  constructor() {
    super();
    this.value = this.lowerValue = this.upperValue = 0;
    this.min = 0;
    this.max = 100;
    this.step = 1;
    this.label = 'Label';
    this.ticks = this.stepMarkers = this._renderedTicks = [];
  }

  updated(changedProperties) {
    if (changedProperties.has('value')) {
      this._updateValue();
      this._tooltipSelection();
    }
    if (changedProperties.has('ticks')) {
      this._thumbTickCollisionCheck = true;
      this._renderTicks();
    }
    if (changedProperties.has('step')) {
      if (!this.showRange) {
        this._renderStepMarkers();
      }
      this._updateValue();
    }
    if (changedProperties.has('min') || changedProperties.has('max')) {
      this._renderTicks();
      if (!this.showRange) {
        this._renderStepMarkers();
      }
      this._updateValue();
      this._tooltipSelection();
    }
    if (changedProperties.has('disabled') && !this.showRange) {
      this._checkTickThumbCollision();
    }
    if (changedProperties.has('_isDrag')) {
      this._isDragObserver();
    }
    if (changedProperties.has('_ticksRendered')) {
      if (this._ticksRendered) {
        if (!this.showRange && this._thumbTickCollisionCheck) {
          this._checkTickThumbCollision();
          this._thumbTickCollisionCheck = false;
        }
        this._ticksRendered = false;
      }
    }
    const listOfProperties = [
      'value',
      'min',
      'max',
      'step',
      'label',
      'showControls',
      'showRange',
      'disabled',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  connectedCallback() {
    super.connectedCallback();
    // Resize Observer
    this.browserResize = new ResizeObserver(() => {
      if (this.vertical) {
        const resizeChecker = this.shadowRoot.querySelector('.vertical-slider-wrapper');
        const input = this.shadowRoot.querySelector('.verticalTrack');
        const resizeCheckerHeight =
          resizeChecker.getBoundingClientRect().height / calculatedBaseFontSize;
        input.style.setProperty('width', `${resizeCheckerHeight}rem`);
        if (this.classList.contains('touch-device')) {
          this.verticalTickCollisionForTouchCheck();
        }
      }
      this._renderTicks();
      if (this.showRange) {
        this.shadowRoot.querySelector('range-slider')._generateHandleTrack();
        this.shadowRoot.querySelector('range-slider')._renderStepMarkersRange();
      } else {
        this._renderStepMarkers();
      }
    });
    this.browserResize.observe(this);
  }

  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    if (!this.showRange) {
      if (this.showControls) {
        this.shadowRoot
          .querySelector('arrow-controls')
          .addEventListener('control-value-change', (e) => {
            e.stopPropagation();
            this.value = e.target.value;
          });
        this.shadowRoot
          .querySelector('arrow-controls')
          .addEventListener('control-value-change-end', (e) => {
            e.stopPropagation();
            this._dispatchValueChangeEndEvent();
          });
      }
      let sliderThumb, tooltip;
      if (this.vertical) {
        sliderThumb = this.shadowRoot.querySelector('.verticalTrack');
        tooltip = this.shadowRoot.querySelector('#verticalTooltip');
      } else {
        sliderThumb = this.shadowRoot.querySelector('.sliderTrack');
        tooltip = this.shadowRoot.querySelector('#sliderTooltip');
      }
      sliderThumb.addEventListener('input', () => {
        this._tooltipSelection();
        this._updateValue();
      });
      sliderThumb.addEventListener('focus', () => this._handleFocus(sliderThumb, tooltip));
      sliderThumb.addEventListener('blur', () => this._handleBlur(sliderThumb, tooltip));
    } else {
      this.shadowRoot.querySelector('range-slider').addEventListener('lower-value-changed', (e) => {
        this.lowerValue = e.target.lowerValue;
      });
      this.shadowRoot.querySelector('range-slider').addEventListener('upper-value-changed', (e) => {
        this.upperValue = e.target.upperValue;
      });
    }
  }

  _handleFocus(thumb, tooltip) {
    thumb.onkeyup = (e) => {
      tooltip.classList.add('tab_tooltip');
      if (e.keyCode === KEYCODE.TAB) {
        thumb.classList.add('tab_outline');
      }
      if (e.keyCode === KEYCODE.ARROW_RIGHT || e.keyCode === KEYCODE.ARROW_UP) {
        this._updateValue();
        this._dispatchValueChangeEndEvent();
      }
      if (e.keyCode === KEYCODE.ARROW_LEFT || e.keyCode === KEYCODE.ARROW_DOWN) {
        this._updateValue();
        this._dispatchValueChangeEndEvent();
      }
    };
  }

  _handleBlur(thumb, tooltip) {
    thumb.classList.remove('tab_outline');
    tooltip.classList.remove('tab_tooltip');
  }

  _renderTicks() {
    if (this.showRange) {
      return;
    }
    let input, containerLength;
    if (this.vertical) {
      input = this.shadowRoot.querySelector('#verticalSlider');
    } else {
      input = this.shadowRoot.querySelector('#horizontalSlider');
    }
    containerLength = input.offsetWidth / calculatedBaseFontSize - 2 * TICK_RADIUS;
    /* _renderedTicks is an array that holds style values (left position) for each tick rendered.
      this is used in the map function in the dom to create ticks based on the passed position for each tick.
    */
    const markers = this.ticks.map((value) => {
      let current_position = (value - this.min) / (this.max - this.min);
      current_position = current_position * containerLength;
      let style;
      style = `left: ${current_position}rem`;
      return { style, position: value };
    });
    this.setReadOnlyProperties({ _renderedTicks: markers });
    this._ticksRendered = true;
  }

  _renderStepMarkers() {
    if (this.step === 0) {
      return;
    }
    const stepTickDistance = 32;
    let input, slider_step, containerLength;
    if (this.vertical) {
      input = this.shadowRoot.querySelector('.verticalTrack');
      slider_step = this.shadowRoot.querySelector('#vertical_step_ticks');
    } else {
      input = this.shadowRoot.querySelector('.sliderTrack');
      slider_step = this.shadowRoot.querySelector('#step_ticks');
    }
    containerLength = input.offsetWidth;
    // calculates the distance between two consecutive step-markers
    let ticksObject = this.getCalculatedStepDistance(
      containerLength,
      this.min,
      this.max,
      this.step
    );
    if (ticksObject.distance >= stepTickDistance) {
      slider_step.classList.add('markers');
      // calculates the step-marker points on track and store it in points array
      this.calculatePoints(containerLength);
    } else {
      slider_step.classList.remove('markers');
    }
  }

  _onTickClick(e) {
    this.value = Number(e.target.dataset.value);
  }

  _updateValue() {
    let input;
    if (!this.showRange) {
      if (this.vertical) {
        input = this.shadowRoot.querySelector('.verticalTrack');
      } else {
        input = this.shadowRoot.querySelector('.sliderTrack');
      }
      this.value = Number(input.value);
    }
  }

  _verticalInputClickActions() {
    this.shadowRoot.querySelector('#verticalMax').classList.add('textbox_click');
    this.shadowRoot.querySelector('#verticalInput').classList.add('text_align');
    this.shadowRoot.querySelector('#verticalInput').focus();
  }

  _verticalInputFousOutActions() {
    this.shadowRoot.querySelector('#verticalMax').classList.remove('textbox_click');
    this.shadowRoot.querySelector('#verticalInput').classList.remove('text_align');
  }

  _tooltipSelection() {
    if (!this.showRange) {
      let sliderThumb, tooltip;
      if (this.vertical) {
        sliderThumb = this.shadowRoot.querySelector('.verticalTrack');
        tooltip = this.shadowRoot.querySelector('#verticalTooltip');
      } else {
        sliderThumb = this.shadowRoot.querySelector('.sliderTrack');
        tooltip = this.shadowRoot.querySelector('#sliderTooltip');
      }
      let newValue = Number(
        ((sliderThumb.value - sliderThumb.min) * 100) / (sliderThumb.max - sliderThumb.min)
      );
      if (Number.isNaN(newValue)) {
        newValue = 0;
      }
      this.setTooltip(newValue, tooltip);
      // checking for thumb collision with ticks on dynamic value change
      this._checkTickThumbCollision();
    }
  }

  _thumbDown() {
    this._isDrag = true;
    if (this.vertical) {
      this.shadowRoot.querySelector('.verticalTrack').classList.add('active');
    } else {
      this.shadowRoot.querySelector('.sliderTrack').classList.add('active');
    }
  }

  _thumbMove() {
    if (this._isDrag) {
      if (!this.showValue) {
        if (this.vertical) {
          this.shadowRoot.querySelector('#verticalTooltip').style.display = 'block';
        } else {
          this.shadowRoot.querySelector('#sliderTooltip').style.display = 'block';
        }
      }
      this._updateValue();
    }
  }

  _thumbUp() {
    if (this._isDrag) {
      this._isDrag = false;
      if (this.vertical) {
        this.shadowRoot.querySelector('#verticalTooltip').style.display = '';
        this.shadowRoot.querySelector('.verticalTrack').classList.remove('active');
      } else {
        this.shadowRoot.querySelector('#sliderTooltip').style.display = '';
        this.shadowRoot.querySelector('.sliderTrack').classList.remove('active');
      }
    }
  }

  _isDragObserver() {
    if (!this._isDrag) {
      this._dispatchValueChangeEndEvent();
    }
  }

  _onKeyPress(e) {
    if (
      !(
        e.keyCode === KEYCODE.ARROW_UP ||
        e.keyCode === KEYCODE.ARROW_DOWN ||
        e.keyCode === KEYCODE.ENTER
      )
    ) {
      return;
    }
    let input = e.target;
    if (e.keyCode === KEYCODE.ARROW_UP) {
      this._valueValidation(input);
      input.focus();
    }
    if (e.keyCode === KEYCODE.ARROW_DOWN) {
      this._valueValidation(input);
      input.focus();
    }
    // If user press ENTER, then do validation
    if (e.keyCode === KEYCODE.ENTER) {
      this._valueValidation(input);
    }
  }

  _handleOnBlur(e) {
    // on blur of input do the validation
    this._valueValidation(e.target);
  }

  _valueValidation(input) {
    let value = input.value;
    input.value = this.getValue(value, this.value, this.min, this.max);
    this.value = Number(input.value);
    //fire custom-event for input-value changed
    this._dispatchValueChangeEndEvent();
  }

  verticalTickCollisionForTouchCheck() {
    const input = this.shadowRoot.querySelector('.verticalTrack');
    const THUMB_RADIUS_PX = convertToPixel(0.4375);
    let containerLength = input.offsetWidth - 2 * THUMB_RADIUS_PX;
    let current_position = (Number(input.value) - this.min) / (this.max - this.min);
    current_position = current_position * containerLength;
    const valuePositionLeft = current_position - convertToPixel(0.8125);
    const valuePositionRight = valuePositionLeft + convertToPixel(2.5);
    const TICK_RADIUS_PX = convertToPixel(0.1875);
    containerLength = input.offsetWidth - 2 * TICK_RADIUS_PX;
    const collisionArray = [];

    this.ticks.map((value) => {
      let current_position = (value - this.min) / (this.max - this.min);
      current_position = current_position * containerLength;
      collisionArray.push(
        this.detectCollision(
          valuePositionLeft,
          valuePositionRight,
          current_position,
          current_position + 2 * TICK_RADIUS_PX
        )
      );
    });

    const trackTicks = this.shadowRoot.querySelectorAll('.tick-item');
    for (let i = 0; i < trackTicks.length; ++i) {
      if (collisionArray[i]) {
        trackTicks[i].classList.add('tick_collision_z');
      } else {
        trackTicks[i].classList.remove('tick_collision_z');
      }
    }
  }

  detectCollision(thumbLeft, thumbRight, tickLeft, tickRight) {
    const noCollision =
      (thumbLeft < tickLeft && thumbRight < tickLeft) ||
      (thumbLeft > tickRight && thumbRight > tickRight);
    return !noCollision;
  }

  _checkTickThumbCollision() {
    // Color the thumb in petrol color if it lands on the tick
    let sliderThumb;
    const trackTicks = this.shadowRoot.querySelectorAll('.tick-item');
    if (this.vertical) {
      sliderThumb = this.shadowRoot.querySelector('.verticalTrack');
    } else {
      sliderThumb = this.shadowRoot.querySelector('.sliderTrack');
    }
    sliderThumb.classList.remove('ticks_collision');
    for (let i = 0; i < trackTicks.length; i++) {
      trackTicks[i].classList.remove('tick_collision_z');
    }
    for (let i = 0; i < trackTicks.length; i++) {
      if (this.value === this.ticks[i] && !this.disabled) {
        trackTicks[i].classList.add('tick_collision_z');
        sliderThumb.classList.add('ticks_collision');
        break;
      }
    }
    if (this.classList.contains('touch-device') && this.vertical) {
      this.verticalTickCollisionForTouchCheck();
    }
  }

  _dispatchValueChangeEndEvent() {
    setTimeout(() => {
      dispatchCustomEvent(this, 'value-change-end', { value: this.value });
    }, 0);
  }
}
customElements.define('sh-slider', SHSlider);
